import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesLeft,
  faAnglesRight,
  faArrowRight,
  faClipboard,
  faClipboardCheck,
  faClock,
  faLifeRing,
  faListCheck,
  faPaperclip,
  faPlus,
  faRotate,
  faSearch,
  faSpinner,
  faTicket,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import config from "../../config";
import { post } from "../../contexts/apicontexts";
import TaskComposerModal from "../Tasks/TasksModels/TaskComposerModal";
import TicketTaskListModal from "./TicketTaskListModal";
import { createTask, fetchEmployees, getTaskList, getTicketTaskCounts } from "../Tasks/taskService";
import {
  formatDateTime,
  getCurrentUserIdentity,
  getIdentityRoleLabel,
  getPriorityClasses,
  getStatusClasses,
  isOwnTicketForIdentity,
  isCustomerAdminIdentity,
  isCustomerIdentity,
  isCustomerUserIdentity,
  isEmployeeIdentity,
} from "./ticketUtils";
import {
  DEFAULT_TICKET_PAGE_SIZE,
  TICKET_PAGE_SIZE_OPTIONS,
  fetchAccessibleCustomers,
  fetchOrganizations,
  fetchTicketPagedList,
} from "./ticketService";

const canManageAllTickets = (identity) =>
  (identity?.normalizedRoles || []).some((role) => {
    return role.includes("admin") || role.includes("support") || role.includes("help") || role.includes("manager") || role.includes("hr");
  });

const emptyForm = (identity) => ({
  subject: "",
  domainUrl: "",
  category: "Technical",
  priority: "Medium",
  contactEmail: identity.email || "",
  userId: identity.userId || "",
  description: "",
  attachmentFile: null,
});

const statusOrder = ["New", "In Progress", "On Hold", "Resolved"];
const MAX_VISIBLE_PAGES = 9;

export default function TicketsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const isCustomerView = useMemo(() => isCustomerIdentity(identity), [identity]);
  const isCustomerAdminView = useMemo(() => isCustomerAdminIdentity(identity), [identity]);
  const isCustomerUserView = useMemo(() => isCustomerUserIdentity(identity), [identity]);
  const isEmployeeView = useMemo(() => isEmployeeIdentity(identity), [identity]);
  const canOpenAdminRequest = useMemo(() => canManageAllTickets(identity), [identity]);
  const isAssignedRoute = location.pathname === "/assigned-tickets";
  const requestedUserId = useMemo(() => new URLSearchParams(location.search).get("userId") || identity.userId || "", [identity.userId, location.search]);
  const showAssignedView = isAssignedRoute || (isEmployeeView && !isCustomerView);
  const identityRoleLabel = useMemo(() => getIdentityRoleLabel(identity), [identity]);

  const [tickets, setTickets] = useState([]);
  const [taskCountsByTicketId, setTaskCountsByTicketId] = useState({});
  const [employees, setEmployees] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [organizationFilter, setOrganizationFilter] = useState("All");
  const [customerFilter, setCustomerFilter] = useState("All");
  const [organizations, setOrganizations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_TICKET_PAGE_SIZE);
  const [pageMeta, setPageMeta] = useState({
    totalCount: 0,
    totalPages: 0,
    pageNumber: 1,
    pageSize: DEFAULT_TICKET_PAGE_SIZE,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [summary, setSummary] = useState({
    total: 0,
    new: 0,
    inProgress: 0,
    onHold: 0,
    resolved: 0,
    myTicketCount: 0,
    unreadTicketCount: 0,
    unreadReplyCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taskSaving, setTaskSaving] = useState(false);
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [taskTicket, setTaskTicket] = useState(null);
  const [taskListTicket, setTaskListTicket] = useState(null);
  const [taskListItems, setTaskListItems] = useState([]);
  const [taskListLoading, setTaskListLoading] = useState(false);
  const [taskListError, setTaskListError] = useState("");
  const [formState, setFormState] = useState(() => emptyForm(identity));

  useEffect(() => {
    setPageNumber(1);
  }, [statusFilter, organizationFilter, customerFilter]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPageNumber(1);
      setSearchText(searchInput.trim());
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const fetchTickets = React.useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetchTicketPagedList({
        assignedView: showAssignedView,
        userId: requestedUserId,
        pageNumber,
        pageSize,
        searchText,
        status: statusFilter,
        organizationId: organizationFilter === "All" ? 0 : Number(organizationFilter),
        customerId: customerFilter === "All" ? 0 : Number(customerFilter),
      });

      setTickets(response.items || []);
      setPageMeta({
        totalCount: response.totalCount || 0,
        totalPages: response.totalPages || 0,
        pageNumber: response.pageNumber || 1,
        pageSize: response.pageSize || DEFAULT_TICKET_PAGE_SIZE,
        hasNextPage: Boolean(response.hasNextPage),
        hasPreviousPage: Boolean(response.hasPreviousPage),
      });
      setSummary(response.summary || {});
      setOrganizations((current) => (showAssignedView || isCustomerUserView || !current.length ? response.organizations || [] : current));
      setCustomers((current) => (showAssignedView || isCustomerUserView || !current.length ? response.customers || [] : current));
    } catch (loadError) {
      setTickets([]);
      setPageMeta((current) => ({
        ...current,
        totalCount: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      }));
      setSummary({
        total: 0,
        new: 0,
        inProgress: 0,
        onHold: 0,
        resolved: 0,
        myTicketCount: 0,
        unreadTicketCount: 0,
        unreadReplyCount: 0,
      });
      setError(loadError?.message || "Failed to load tickets.");
    } finally {
      setLoading(false);
    }
  }, [customerFilter, isCustomerUserView, organizationFilter, pageNumber, pageSize, requestedUserId, searchText, showAssignedView, statusFilter]);

  const fetchTaskCounts = React.useCallback(async () => {
    if (isCustomerView) {
      setTaskCountsByTicketId({});
      return;
    }

    try {
      setTaskCountsByTicketId(await getTicketTaskCounts(showAssignedView ? { scopeUserId: requestedUserId } : {}));
    } catch {
      setTaskCountsByTicketId({});
    }
  }, [isCustomerView, requestedUserId, showAssignedView]);

  useEffect(() => {
    fetchTickets();
    fetchTaskCounts();
  }, [fetchTickets, fetchTaskCounts]);

  useEffect(() => {
    let active = true;

    const loadFilterOptions = async () => {
      if (showAssignedView || isCustomerUserView) return;

      try {
        const [organizationItems, customerItems] = await Promise.all([
          canOpenAdminRequest ? fetchOrganizations() : Promise.resolve([]),
          canOpenAdminRequest ? fetchAccessibleCustomers("all") : fetchAccessibleCustomers("mine"),
        ]);

        if (!active) return;

        setOrganizations(
          canOpenAdminRequest
            ? organizationItems
            : []
        );
        setCustomers(customerItems);
      } catch {
        if (!active) return;
        setOrganizations([]);
        setCustomers([]);
      }
    };

    loadFilterOptions();

    return () => {
      active = false;
    };
  }, [canOpenAdminRequest, isCustomerUserView, showAssignedView]);

  useEffect(() => {
    if (isCustomerView) return;

    let cancelled = false;

    const loadEmployees = async () => {
      const items = await fetchEmployees();
      if (!cancelled) setEmployees(items);
    };

    loadEmployees();

    return () => {
      cancelled = true;
    };
  }, [isCustomerView]);

  const counts = useMemo(
    () => ({
      total: summary.total || 0,
      New: summary.new || 0,
      "In Progress": summary.inProgress || 0,
      "On Hold": summary.onHold || 0,
      Resolved: summary.resolved || 0,
    }),
    [summary]
  );

  const myTicketCount = summary.myTicketCount || 0;
  const unreadTicketCount = summary.unreadTicketCount || 0;
  const unreadReplyCount = summary.unreadReplyCount || 0;

  const visibleCustomers = useMemo(() => {
    if (organizationFilter === "All") return customers;
    return customers.filter((item) => Number(item.organizationId || 0) === Number(organizationFilter));
  }, [customers, organizationFilter]);

  useEffect(() => {
    if (customerFilter === "All") return;
    if (visibleCustomers.some((item) => String(item.id) === String(customerFilter))) return;
    setCustomerFilter("All");
  }, [customerFilter, visibleCustomers]);

  const paginationNumbers = useMemo(
    () => buildVisiblePageNumbers(pageMeta.pageNumber, pageMeta.totalPages, MAX_VISIBLE_PAGES),
    [pageMeta.pageNumber, pageMeta.totalPages]
  );

  const rangeLabel = useMemo(() => {
    if (!pageMeta.totalCount || !tickets.length) return "Showing 0 tickets";

    const start = (pageMeta.pageNumber - 1) * pageMeta.pageSize + 1;
    const end = start + tickets.length - 1;
    return `Showing ${start}-${end} of ${pageMeta.totalCount}`;
  }, [pageMeta.pageNumber, pageMeta.pageSize, pageMeta.totalCount, tickets.length]);

  const resetForm = () => {
    setFormState(emptyForm(identity));
    setFormErrors({});
    setError("");
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formState.subject.trim()) nextErrors.subject = "Subject is required.";
    if (!formState.category) nextErrors.category = "Category is required.";
    if (!formState.priority) nextErrors.priority = "Priority is required.";
    if (!formState.contactEmail.trim()) nextErrors.contactEmail = "Contact email is required.";
    else if (!/\S+@\S+\.\S+/.test(formState.contactEmail.trim())) nextErrors.contactEmail = "Contact email is invalid.";
    if (!formState.userId.trim()) nextErrors.userId = "Logged in user ID is missing.";
    if (!formState.description.trim()) nextErrors.description = "Description is required.";

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCreateTicket = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError("");

    const formData = new FormData();
    formData.append("Subject", formState.subject.trim());
    formData.append("DomainUrl", formState.domainUrl.trim());
    formData.append("Category", formState.category);
    formData.append("Priority", formState.priority);
    formData.append("ContactEmail", formState.contactEmail.trim());
    formData.append("UserId", formState.userId.trim());
    formData.append("Description", formState.description.trim());

    if (formState.attachmentFile) {
      formData.append("AttachmentFile", formState.attachmentFile);
    }

    try {
      const response = await post(`${config.baseurl}Ticket/add`, formData);

      if (response?.status === 200) {
        setIsCreateOpen(false);
        resetForm();
        await fetchTickets();
        navigate(`/tickets/${response.data.id}`, { state: { backTo: "/tickets" } });
        return;
      }

      setError(response?.message || "Failed to create ticket.");
    } catch {
      setError("Failed to create ticket.");
    } finally {
      setSaving(false);
    }
  };

  const openTaskComposer = (ticket, options = {}) => {
    if (!options.skipConfirm && !window.confirm(`Create a task from ticket ${ticket.ticketNumber || ""}?`)) return;
    setTaskTicket(ticket);
  };

  const closeTaskListModal = () => {
    setTaskListTicket(null);
    setTaskListItems([]);
    setTaskListLoading(false);
    setTaskListError("");
  };

  const openTaskListModal = async (ticket) => {
    const ticketId = Number(ticket?.id || 0);

    setTaskListTicket(ticket);
    setTaskListItems([]);
    setTaskListLoading(true);
    setTaskListError("");

    try {
      setTaskListItems(await getTaskList({ ticketId }));
    } catch {
      setTaskListError("Ticket task list could not be loaded.");
    } finally {
      setTaskListLoading(false);
    }
  };

  const handleCreateTask = async (values) => {
    if (!taskTicket) return;

    setTaskSaving(true);
    setError("");

    try {
      await createTask(
        {
          ...values,
          ticketId: Number(taskTicket.id || 0),
          ticketNumber: taskTicket.ticketNumber || "",
          ticketSubject: taskTicket.subject || "",
          ticketStatus: taskTicket.status || "",
          sourceType: "SupportTicket",
          createdByUserId: identity.userId,
          createdByName: identity.fullName || identity.email || "User",
        },
        identity
      );

      setTaskTicket(null);
      await fetchTaskCounts();

      if (taskListTicket && Number(taskListTicket.id || 0) === Number(taskTicket.id || 0)) {
        setTaskListItems(await getTaskList({ ticketId: Number(taskTicket.id || 0) }));
      }
    } catch {
      setError("Task could not be created from this ticket.");
    } finally {
      setTaskSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <Breadcrumb
        pageName={showAssignedView ? "Assigned Tickets" : "Support Tickets"}
        currentPage={showAssignedView ? "Assigned Tickets" : "Support Tickets"}
      />

      <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.22),_transparent_30%),linear-gradient(135deg,#0f172a,#1e293b_58%,#334155)] px-5 py-6 text-white md:px-7 md:py-8">
          <div className="absolute right-0 top-0 h-36 w-36 translate-x-10 -translate-y-8 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-100">
                <FontAwesomeIcon icon={faLifeRing} />
                {showAssignedView ? "Assigned Ticket Desk" : isCustomerView ? "Customer Support" : "Support Desk"}
              </div>
              {/* <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">{pageTitle}</h1> */}
              {/* <p className="mt-3 max-w-2xl text-sm text-slate-200 md:text-base">{pageDescription}</p> */}
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-200">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">Logged in as {identity.fullName || "User"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{identityRoleLabel}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{identity.email || "No email found"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">User ID: {identity.userId || "Unavailable"}</span>
                {isCustomerView ? (
                  <span className="rounded-full border border-amber-300/30 bg-amber-400/15 px-3 py-1 text-amber-50">
                    Customer-scoped access enabled
                  </span>
                ) : null}
                {unreadReplyCount ? (
                  <span className="rounded-full border border-emerald-300/30 bg-emerald-400/15 px-3 py-1 text-emerald-50">
                    {unreadReplyCount} unread {unreadReplyCount === 1 ? "update" : "updates"}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {!showAssignedView && isCustomerView ? (
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setIsCreateOpen(true);
                  }}
                  className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg transition hover:bg-amber-300"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  New Ticket
                </button>
              ) : null}
              {!isCustomerView ? (
                <button
                  type="button"
                  onClick={() => navigate("/my-tasks")}
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
                >
                  <FontAwesomeIcon icon={faListCheck} />
                  Open Tasks
                </button>
              ) : null}
              {!showAssignedView && canOpenAdminRequest ? (
                <>
                  <button
                    type="button"
                    onClick={() => navigate("/tickets/admin/new-request")}
                    className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-amber-300"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Open Support Request
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/tickets/admin-created")}
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
                  >
                    <FontAwesomeIcon icon={faClipboard} />
                    Admin Created Tickets
                  </button>
                </>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  fetchTickets();
                  fetchTaskCounts();
                }}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 border-b border-slate-200 bg-slate-50/80 p-5 md:grid-cols-5 md:p-6">
          <StatCard label="Total" value={counts.total} icon={faTicket} onClick={() => setStatusFilter("All")} isActive={statusFilter === "All"} />
          <StatCard label="New" value={counts["New"] || 0} icon={faClipboard} onClick={() => setStatusFilter("New")} isActive={statusFilter === "New"} />
          <StatCard label="In Progress" value={counts["In Progress"] || 0} icon={faClock} onClick={() => setStatusFilter("In Progress")} isActive={statusFilter === "In Progress"} />
          <StatCard label="On Hold" value={counts["On Hold"] || 0} icon={faSpinner} onClick={() => setStatusFilter("On Hold")} isActive={statusFilter === "On Hold"} />
          <StatCard label="Resolved" value={counts["Resolved"] || 0} icon={faArrowRight} onClick={() => setStatusFilter("Resolved")} isActive={statusFilter === "Resolved"} />
        </div>

        <div className="p-5 md:p-6">
          <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{showAssignedView ? "Assigned Ticket Inbox" : "Ticket Inbox"}</h2>
              <p className="mt-1 text-sm text-slate-500">
                {showAssignedView
                  ? "Every assigned ticket shows status, priority, requester, customer, organization, and current linked task count."
                  : isCustomerView
                    ? "Start with your own tickets, switch to all customer tickets when needed, and watch unread replies from one place."
                    : "Every ticket shows status, priority, requester context, and the latest activity."}
              </p>
              {!showAssignedView && isCustomerView ? (
                <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-600">
                    My tickets: {myTicketCount}
                  </span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-600">
                    All accessible: {tickets.length}
                  </span>
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-700">
                    Needs attention: {unreadTicketCount}
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
              >
                <option value="All">All Status</option>
                {statusOrder.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>

              <select
                value={organizationFilter}
                onChange={(event) => setOrganizationFilter(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
              >
                <option value="All">All Organizations</option>
                {organizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>
                    {organization.name}
                  </option>
                ))}
              </select>

              <select
                value={customerFilter}
                onChange={(event) => setCustomerFilter(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
              >
                <option value="All">All Customers</option>
                {visibleCustomers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>

              <div className="relative min-w-[220px]">
                <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search subject, requester, customer, or domain"
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                />
              </div>

              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value) || DEFAULT_TICKET_PAGE_SIZE);
                  setPageNumber(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
              >
                {TICKET_PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    Page Size: {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-sm font-medium text-slate-500">{rangeLabel}</div>
          </div>

          {error ? (
            <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
          ) : null}

          {loading ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-6 py-14 text-center text-sm text-slate-500">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading tickets...
            </div>
          ) : tickets.length ? (
            <div className="space-y-3">
              {tickets.map((ticket) => {
                const taskCount = taskCountsByTicketId[Number(ticket.id || 0)] || 0;
                const isOwnTicket = isOwnTicketForIdentity(ticket, identity);
                const unreadLabel = ticket.unreadCount === 1 ? "1 unread update" : `${ticket.unreadCount} unread updates`;

                return (
                  <div
                    key={ticket.id}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate(`/tickets/${ticket.id}`, {
                        state: {
                          backTo: isAssignedRoute ? `${location.pathname}${location.search}` : "/tickets",
                        },
                      })
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(`/tickets/${ticket.id}`, {
                          state: {
                            backTo: isAssignedRoute ? `${location.pathname}${location.search}` : "/tickets",
                          },
                        });
                      }
                    }}
                    className={`w-full cursor-pointer rounded-2xl border p-4 text-left transition hover:border-slate-300 hover:shadow-md ${ticket.hasUnread ? "border-amber-300 bg-amber-50/40" : "border-slate-200 bg-white"
                      }`}
                  >
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                            {ticket.ticketNumber}
                          </span>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusClasses(ticket.status)}`}>
                            {ticket.status}
                          </span>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getPriorityClasses(ticket.priority)}`}>
                            {ticket.priority}
                          </span>
                          {ticket.organizationName ? (
                            <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">
                              {ticket.organizationName}
                            </span>
                          ) : null}
                          {ticket.customerName ? (
                            <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-700">
                              {ticket.customerName}
                            </span>
                          ) : null}
                          {isCustomerView ? (
                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${isOwnTicket
                              ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border border-slate-200 bg-slate-100 text-slate-600"
                              }`}>
                              {isOwnTicket ? "My ticket" : "Shared ticket"}
                            </span>
                          ) : null}
                          {ticket.hasUnread ? (
                            <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                              {unreadLabel}
                            </span>
                          ) : null}
                          {isCustomerUserView && !ticket.canReply ? (
                            <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                              View only
                            </span>
                          ) : isCustomerUserView ? (
                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                              Reply allowed
                            </span>
                          ) : null}
                          {!isCustomerView ? (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openTaskListModal(ticket);
                              }}
                              className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
                            >
                              {taskCount} {taskCount === 1 ? "Task" : "Tasks"}
                            </button>
                          ) : null}
                        </div>
                        <h3 className="truncate text-base font-semibold text-slate-900">{ticket.subject}</h3>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-500">
                          <span>{ticket.category}</span>
                          <span>{ticket.contactEmail}</span>
                          <span>{ticket.domainUrl || "No domain provided"}</span>
                        </div>
                        <div className="mt-3 grid gap-2 text-xs text-slate-500 md:grid-cols-2">
                          <span>Requester: {ticket.requesterName || ticket.userName || "Not available"}</span>
                          <span>Requester ID: {ticket.requesterUserId || ticket.userId || "Not available"}</span>
                          {ticket.customerName ? <span>Customer: {ticket.customerName}</span> : null}
                          {ticket.organizationName ? <span>Organization: {ticket.organizationName}</span> : null}
                        </div>
                      </div>

                      <div className="shrink-0 text-right text-xs text-slate-500">
                        <div>Opened {formatDateTime(ticket.createdAt)}</div>
                        <div className="mt-1">Last update {formatDateTime(ticket.lastReplyAt || ticket.createdAt)}</div>
                        {ticket.hasUnread ? (
                          <div className="mt-2 font-semibold text-amber-700">Unread activity waiting</div>
                        ) : (
                          <div className="mt-2 text-slate-400">Up to date</div>
                        )}
                        <div className="mt-3 flex flex-wrap justify-end gap-2">
                          {!isCustomerView ? (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openTaskComposer(ticket);
                              }}
                              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                            >
                              <FontAwesomeIcon icon={faClipboardCheck} />
                              Make Task
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/tickets/${ticket.id}`, {
                                state: {
                                  backTo: isAssignedRoute ? `${location.pathname}${location.search}` : "/tickets",
                                },
                              });
                            }}
                            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-800"
                          >
                            Open details
                            <FontAwesomeIcon icon={faArrowRight} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white">
                <FontAwesomeIcon icon={faTicket} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">No tickets found</h3>
              <p className="mt-2 text-sm text-slate-500">
                {showAssignedView
                  ? "There are no customer tickets assigned to your login right now."
                  : isCustomerUserView
                    ? "Your customer ticket list is empty right now."
                    : isCustomerAdminView
                      ? "No ticket matches the selected customer filters for this account yet."
                      : "Create your first ticket to start the support conversation."}
              </p>
              {!showAssignedView && isCustomerView ? (
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setIsCreateOpen(true);
                  }}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  New Ticket
                </button>
              ) : null}
            </div>
          )}

          {tickets.length ? (
            <div className="mt-5 flex flex-col gap-4 border-t border-slate-200 pt-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="text-sm text-slate-500">{rangeLabel}</div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPageNumber(1)}
                  disabled={!pageMeta.hasPreviousPage}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faAnglesLeft} />
                </button>
                {paginationNumbers.map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setPageNumber(page)}
                    className={`inline-flex h-10 min-w-10 items-center justify-center rounded-full border px-3 text-sm font-semibold transition ${
                      page === pageMeta.pageNumber
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPageNumber(pageMeta.totalPages || 1)}
                  disabled={!pageMeta.hasNextPage}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faAnglesRight} />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {isCreateOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-3 sm:p-4">
          <div className="absolute inset-0 bg-slate-950/65 backdrop-blur-sm" onClick={() => setIsCreateOpen(false)} />
          <div className="relative z-10 my-4 flex max-h-[calc(100vh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl sm:my-6 sm:max-h-[calc(100vh-3rem)]">
            <div className="border-b border-slate-200 bg-slate-950 px-4 py-5 text-white sm:px-6">
              <button
                type="button"
                onClick={() => setIsCreateOpen(false)}
                className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <div className="pr-12">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-300">Create Ticket</p>
                <h3 className="mt-2 text-xl font-semibold sm:text-2xl">Open a new support request</h3>
                <p className="mt-2 text-sm text-slate-300">The ticket will be created under your logged-in customer account.</p>
              </div>
            </div>

            <div className="grid flex-1 gap-5 overflow-y-auto p-4 sm:p-6 md:grid-cols-2">
              <Field label="Subject" required error={formErrors.subject}>
                <input
                  value={formState.subject}
                  onChange={(event) => setFormState((current) => ({ ...current, subject: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                  placeholder="Example: Billing issue on renewal invoice"
                />
              </Field>

              <Field label="Domain URL">
                <input
                  value={formState.domainUrl}
                  onChange={(event) => setFormState((current) => ({ ...current, domainUrl: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                  placeholder="https://example.com"
                />
              </Field>

              <Field label="Category" required error={formErrors.category}>
                <select
                  value={formState.category}
                  onChange={(event) => setFormState((current) => ({ ...current, category: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                >
                  <option value="Billing">Billing</option>
                  <option value="Technical">Technical</option>
                  <option value="General">General</option>
                </select>
              </Field>

              <Field label="Priority" required error={formErrors.priority}>
                <select
                  value={formState.priority}
                  onChange={(event) => setFormState((current) => ({ ...current, priority: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </Field>

              <Field label="Contact Email" required error={formErrors.contactEmail}>
                <input
                  value={formState.contactEmail}
                  onChange={(event) => setFormState((current) => ({ ...current, contactEmail: event.target.value }))}
                  readOnly={Boolean(identity.email)}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400 read-only:bg-slate-50"
                  placeholder="customer@example.com"
                />
              </Field>

              <Field label="User ID" required error={formErrors.userId}>
                <input
                  value={formState.userId}
                  onChange={(event) => setFormState((current) => ({ ...current, userId: event.target.value }))}
                  readOnly
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none"
                />
              </Field>

              <div className="md:col-span-2">
                <Field label="Description" required error={formErrors.description}>
                  <textarea
                    value={formState.description}
                    onChange={(event) => setFormState((current) => ({ ...current, description: event.target.value }))}
                    className="min-h-[140px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                    placeholder="Describe the issue, error steps, affected area, and anything support should know."
                  />
                </Field>
              </div>

              <div className="md:col-span-2">
                <Field label="File Upload">
                  <label className="flex cursor-pointer flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center transition hover:border-slate-400 hover:bg-slate-100">
                    <FontAwesomeIcon icon={faPaperclip} className="mb-3 text-slate-500" />
                    <span className="text-sm font-semibold text-slate-700">
                      {formState.attachmentFile ? formState.attachmentFile.name : "Attach screenshot, invoice, or log file"}
                    </span>
                    <span className="mt-1 text-xs text-slate-500">Click to choose a file</span>
                    <input
                      type="file"
                      className="hidden"
                      onChange={(event) =>
                        setFormState((current) => ({
                          ...current,
                          attachmentFile: event.target.files?.[0] || null,
                        }))
                      }
                    />
                  </label>
                </Field>
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-6 sm:py-5">
              <button
                type="button"
                onClick={() => setIsCreateOpen(false)}
                className="w-full rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-white sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateTicket}
                disabled={saving}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-70 sm:w-auto"
              >
                <FontAwesomeIcon icon={saving ? faSpinner : faPlus} spin={saving} />
                {saving ? "Creating..." : "Create Ticket"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <TaskComposerModal
        open={Boolean(taskTicket)}
        ticket={taskTicket}
        employees={employees}
        identity={identity}
        submitting={taskSaving}
        onClose={() => setTaskTicket(null)}
        onSubmit={handleCreateTask}
      />

      <TicketTaskListModal
        open={Boolean(taskListTicket)}
        ticket={taskListTicket}
        tasks={taskListItems}
        loading={taskListLoading}
        error={taskListError}
        onClose={closeTaskListModal}
        onCreateTask={() => {
          if (!taskListTicket) return;
          closeTaskListModal();
          openTaskComposer(taskListTicket, { skipConfirm: true });
        }}
      />
    </div>
  );
}

function StatCard({ label, value, icon, onClick, isActive }) {
  return (
    <div
      onClick={onClick}
      className={`rounded-2xl border bg-white p-4 shadow-sm transition ${onClick ? "cursor-pointer hover:border-slate-400" : ""
        } ${isActive ? "border-slate-800 ring-1 ring-slate-800" : "border-slate-200"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-500">{label}</div>
          <div className="mt-1 text-3xl font-semibold text-slate-900">{value}</div>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white">
          <FontAwesomeIcon icon={icon} />
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {error ? <div className="mt-2 text-xs font-medium text-rose-600">{error}</div> : null}
    </div>
  );
}

function buildVisiblePageNumbers(currentPage, totalPages, maxVisiblePages) {
  if (!totalPages) return [];

  const safeMax = Math.max(1, maxVisiblePages);
  let startPage = Math.max(1, currentPage - Math.floor(safeMax / 2));
  let endPage = startPage + safeMax - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - safeMax + 1);
  }

  const pages = [];
  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }

  return pages;
}
